"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { ArrowRight, Play, ShieldCheck, Zap, Activity, Signal, BatteryCharging, Database, Clock, Globe } from "lucide-react";
import DashboardMockup from "./DashboardMockup";

// Pending experiment IDs (market-test-plan.md §3)
const DASHBOARD_HERO = "wf_homepage_dashboard_hero_v1";
const DARK_PREMIUM_DESIGN = "wf_dark_premium_dashboard_v1";
const COPY_CONTROL = "copy_premium_control_vs_trust_v1";
const PROOF_APP = "proof_app_reviews_v1";

/**
 * Re-digital dark hero per generated_homepage.png — premium app positioning:
 * eyebrow chip + big white headline with a mint→cyan gradient keyword + Get
 * Started gradient pill + "How it works" + trust row | the app DASHBOARD mockup
 * with floating stat chips. Claim-safe trust row (no ratings/counts).
 */
export default function Hero() {
  const { t } = useTranslations();
  const router = useRouter();

  // Exposure events for market-test experiments (consent-gated, best-effort)
  useEffect(() => {
    for (const id of [DASHBOARD_HERO, DARK_PREMIUM_DESIGN, COPY_CONTROL, PROOF_APP]) {
      try {
        track({
          event_name: "page_view",
          event_id: `page_view:${id}`,
          experiment_id: id,
        });
      } catch { /* best-effort + consent-gated */ }
    }
  }, []);

  const trust = [
    { icon: ShieldCheck, label: t("dp.hero.t1", "Secure & encrypted") },
    { icon: Zap, label: t("dp.hero.t2", "Instant activation") },
    { icon: Activity, label: t("dp.hero.t3", "Data control made simple") },
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="container mx-auto px-4 pt-10 pb-14 lg:pt-16 lg:pb-20 grid lg:grid-cols-2 gap-10 items-center">
        {/* copy */}
        <div className="relative z-10">
          <span className="inline-flex items-center gap-2 dp-chip px-3 py-1 text-xs font-semibold text-foreground/80">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" /> {t("dp.hero.eyebrow", "Instant eSIM · No plastic · No roaming surprises")}
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            {t("dp.hero.title1", "Global")} <span className="dp-neon">{t("dp.hero.title2", "connectivity,")}</span><br className="hidden sm:block" /> {t("dp.hero.title3", "one tap away.")}
          </h1>
          <p className="mt-5 text-lg text-muted-foreground max-w-xl">
            {t("dp.hero.sub", "Instant travel eSIM data in 200+ destinations. Premium, secure and built for every journey.")}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => router.push("/destinations/")} className="dp-pill inline-flex items-center gap-2 h-12 rounded-full px-7 font-bold transition-all active:scale-[0.98]">
              {t("dp.hero.cta", "Get Started")} <ArrowRight className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => router.push("/installation-guide/")} className="inline-flex items-center gap-2 h-12 rounded-full px-5 font-bold text-foreground border border-border hover:border-primary/50 transition-all hover:-translate-y-0.5 active:scale-[0.98]">
              <Play className="w-4 h-4 text-primary" /> {t("dp.hero.secondary", "How it works")}
            </button>
          </div>

          <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
            {trust.map(({ icon: Icon, label }) => (
              <li key={label} className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80"><Icon className="w-4 h-4 text-primary" />{label}</li>
            ))}
          </ul>
        </div>

        {/* dashboard + floating chips */}
        <div className="relative">
          <DashboardMockup />
          <div className="hidden md:flex absolute -left-4 top-8 dp-card chip-float px-3 py-2 items-center gap-2">
            <Database className="w-4 h-4 text-primary" />
            <div><div className="text-xs font-bold text-foreground">{t("dp.hero.chipA", "15GB remaining")}</div><div className="text-[10px] text-muted-foreground">{t("dp.hero.chipAopt", "Of 20GB plan")}</div></div>
          </div>
          <div className="hidden md:flex absolute -right-3 top-4 dp-card chip-float-delayed px-3 py-2 items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <div><div className="text-xs font-bold text-foreground">{t("dp.hero.chipB", "12 days left")}</div><div className="text-[10px] text-muted-foreground">{t("dp.hero.chipBopt", "Active plan")}</div></div>
          </div>
          <div className="hidden md:flex absolute left-0 bottom-12 dp-card chip-float-slow px-3 py-2 items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            <div><div className="text-xs font-bold text-foreground">{t("dp.hero.chipC", "3 countries")}</div><div className="text-[10px] text-muted-foreground">{t("dp.hero.chipCopt", "Asia Regional")}</div></div>
          </div>
          <div className="hidden md:flex absolute -right-2 bottom-24 dp-card chip-float-delayed px-3 py-2 items-center gap-2">
            <Signal className="w-4 h-4 text-primary" />
            <div><div className="text-xs font-bold text-foreground">{t("dp.hero.chipD", "Fast & reliable")}</div><div className="text-[10px] text-muted-foreground">4G / 5G</div></div>
          </div>
          <div className="hidden md:flex absolute -left-3 bottom-8 dp-card chip-float-slow px-3 py-2 items-center gap-2">
            <BatteryCharging className="w-4 h-4 text-primary" />
            <div><div className="text-xs font-bold text-foreground">{t("dp.hero.chipE", "Battery friendly")}</div><div className="text-[10px] text-muted-foreground">{t("dp.hero.chipEopt", "Optimized")}</div></div>
          </div>
        </div>
      </div>
    </section>
  );
}
